import promiseRetry from 'promise-retry';
import {PureComponent, Fragment} from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import Video, {Room, isSupported, createLocalTracks} from 'twilio-video';

import {Loader} from 'components';
import {InformationModal} from 'components/modals';
import {NOT_OFFICIALLY_SUPPORTED, BROWSER_SUPPORT_ERROR, TWILIO_SUPPORTED_BROWSERS_LINK} from 'constants/webrtc';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {MediaDevicesConstraints, WebRTCConnectDetails} from 'types';
import {browserUtils, logger, mediaDevicesUtils, platformUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.WEBRTC;

type ChildrenProps = {
    room: Room;
}

type Props = {
    readonly roomName: string;
    readonly mediaConstraints?: MediaDevicesConstraints;
    readonly ignoreErrorDialog?: boolean;
    readonly logLevel?: Video.LogLevel;
    readonly onEnumerateMediaDevices?: (devices: MediaDeviceInfo[]) => void;
    readonly onEnumerateTracks?: (tracks: MediaStreamTrack[]) => void;
    readonly onWebRTCStartSuccess?: (details?: WebRTCConnectDetails) => void;
    readonly onWebRTCStartFailed?: (details: WebRTCConnectDetails) => void;
    readonly getTokenFn: () => Promise<{token: string}>;
    readonly onDisconnect?: () => void;
    readonly children: (data: ChildrenProps) => React.ReactNode;
} & WithTranslation;

type State = {
    room: Room,
    errorDialogProps: {
        open: boolean;
        message: string;
    },
    localTracks: Video.LocalTrack[];
    mediaRecorder: MediaRecorder;
    failedConnectDetails: WebRTCConnectDetails;
};

const TWILIO_SIGNALING_CONNECTION_TIMEOUT_CODE = 53002;
const TWILIO_ROOM_CONNECT_FAILED_CODE = 53104;
const SHOULD_RECONNECT_TWILIO_ERROR_CODES = [TWILIO_SIGNALING_CONNECTION_TIMEOUT_CODE, TWILIO_ROOM_CONNECT_FAILED_CODE];

const initialState: State = {
    room: null,
    errorDialogProps: {
        open: false,
        message: ''
    },
    localTracks: [],
    mediaRecorder: null,
    failedConnectDetails: null
};

class TwilioRoom extends PureComponent<Props, Readonly<State>> {
    readonly state = initialState;
    _isMounted = false;
    connectPromise: ReturnType<typeof Video.connect> = null;

    get isNotOfficiallySupported() {
        return !isSupported && browserUtils.isWebRTCSupported();
    }

    get isMediaDevicesEnumerated(): boolean {
        return !!this.props.mediaConstraints?.audioinput || !!this.props.mediaConstraints?.videoinput;
    }

    async componentDidMount() {
        this._isMounted = true;
        window.addEventListener('beforeunload', this.disconnectRoom);
        window.addEventListener('pagehide', this.disconnectRoom);

        try {
            this.checkPlatform();
            if (this.isMediaDevicesEnumerated) {
                await this.getUserMedia();
            }
            this.start();
        } catch (error: any) {
            logger.warn('Twilio - Room connection failed', error.message);
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (mediaDevicesUtils.isUsedInputDevicesToggled(prevProps.mediaConstraints, this.props.mediaConstraints)
            && this.isMediaDevicesEnumerated) {
            // getUserMedia logs the failure and raises the error dialog itself, so the rejection is only swallowed
            // here — unhandled, it reaches the console as "Uncaught (in promise)" on every activity switch
            void this.getUserMedia({publish: true}).catch(() => null);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.props.onEnumerateMediaDevices?.([]);
        this.props.onEnumerateTracks?.([]);
        this.disconnectRoom();
        window.removeEventListener('beforeunload', this.disconnectRoom);
        window.removeEventListener('pagehide', this.disconnectRoom);
    }

    checkPlatform = () => {
        if (!isSupported && !browserUtils.isWebRTCSupported()) {
            const message = 'Browser does not support required capabilities';
            logger.warn({category: logger.category.Twilio, message});
            this.webRTCStartFailed({
                code: BROWSER_SUPPORT_ERROR,
                message,
                attempt: 1
            }, T_KEYS.ROOM_ERROR_DIALOG.NOT_SUPPORTED);
            throw new Error(message);
        }
    };

    getUserMedia = async (options: {publish?: boolean} = {}) => {
        try {
            const {audioinput, videoinput} = this.props.mediaConstraints || {};
            // Camera is requested only when the running activity needs video, otherwise the browser shows a
            // combined microphone + camera permission prompt for audio-only activities
            const enumerateDevices = {
                ...audioinput && {audio: audioinput},
                ...videoinput && {
                    video: {
                        width: {ideal: 960},
                        height: {ideal: 720},
                        ...videoinput,
                    } as MediaTrackConstraints
                }
            };
            const userMedia = await navigator.mediaDevices.getUserMedia(enumerateDevices);
            userMedia.getTracks().forEach((track) => {
                track.stop();
            });
            const devices = await navigator.mediaDevices.enumerateDevices();
            const mediaDevices = mediaDevicesUtils.getMediaDevices(devices, this.props.t);
            const localTracks = await createLocalTracks(enumerateDevices);
            const localAudioTrack = (localTracks.find((track) => track.kind === 'audio') as Video.LocalAudioTrack)?.mediaStreamTrack;
            this.setState({localTracks});
            this.props.onEnumerateMediaDevices?.(mediaDevices);
            this.props.onEnumerateTracks?.([localAudioTrack]);
            if (options?.publish) {
                this.state.room?.localParticipant?.publishTracks?.(localTracks);
            }
        } catch (error: any) {
            logger.warn({category: logger.category.Twilio, message: 'cannot obtain camera & microphone access'}, error.message);
            const {code, message, dialogKey} = mediaDevicesUtils.getUserMediaErrorDetails(error);
            this.webRTCStartFailed({code, message, attempt: 1}, dialogKey);
            throw error;
        }
    };

    start = (retryParams: { retries: number, minTimeout: number } = {retries: 5, minTimeout: 2000}): Promise<any> =>
        this.getTokenWithRetries(retryParams)
            .then(({token}) => {
                this.joinRoomWithRetries(token, retryParams);
            });

    getTokenWithRetries = (retryParams: { retries: number, minTimeout: number }) => {
        const {getTokenFn} = this.props;
        return promiseRetry((retry, attempt) => {
            if (attempt > 1) {
                logger.warn({category: logger.category.Twilio, message: `get token attempt number: ${attempt}`});
            }
            return getTokenFn().catch(((error) => {
                this.webRTCStartFailed({
                    attempt,
                    code: error.name,
                    message: String(error)
                });
                return retry(error);
            }));
        }, retryParams).catch((error) => {
            logger.warn({category: logger.category.Twilio, message: 'cannot obtain token for room'}, error.message);
            this.webRTCStartFailed({
                attempt: retryParams.retries,
                code: error.name,
                message: String(error)
            }, String(error));
            throw error;
        });
    };

    joinRoomWithRetries = (
        token: string,
        retryParams: { retries: number, minTimeout: number },
        notify = true
    ) => promiseRetry((retry, attempt) => {
        if (attempt > 1) {
            logger.warn({category: logger.category.Twilio, message: `connection attempt number: ${attempt}`});
        }
        if (this.props.roomName && this._isMounted) {
            return this.joinRoom(token, notify).catch((error) => {
                // Unmounting cancels the pending connect on purpose, which rejects here. Reporting that as a
                // connection failure would raise a dialog and skew the WebRTC failure stats with a non-failure.
                if (notify && this._isMounted) {
                    this.webRTCStartFailed({
                        attempt,
                        code: error.name,
                        message: String(error)
                    });
                }
                return retry(error);
            });
        }
        return Promise.resolve();
    }, retryParams).catch((error) => {
        logger.warn({category: logger.category.Twilio, message: 'cannot join the room. '}, error.message);
        if (notify) {
            this.webRTCStartFailed({
                // TODO: attempt is not correct here
                attempt: retryParams.retries,
                code: error.name,
                message: String(error)
            }, T_KEYS.ROOM_ERROR_DIALOG.UNEXPECTED_ERROR);
        }
    });

    webRTCStartSuccess = () => {
        this.props.onWebRTCStartSuccess(this.getTwilioStartSuccessStatus((this.state.failedConnectDetails?.attempt || 0) + 1));
        this.setState({failedConnectDetails: null});
    };

    webRTCStartFailed = (details: WebRTCConnectDetails, message?: string) => {
        if (message && !this.props.ignoreErrorDialog) {
            this.openErrorDialog(message);
        }
        this.setState({failedConnectDetails: details});
        this.props.onWebRTCStartFailed?.(details);
    };

    disconnectRoom = () => {
        const {room} = this.state;

        // A connect() still in flight keeps negotiating against a room the server is already tearing down, and the
        // late remote description it then receives raises TwilioError 53402 from inside the SDK's own promise chain —
        // unreachable by our .catch, so it surfaces as "Uncaught (in promise)". Cancelling disconnects the transport
        // and closes the peer connection, so that description never arrives.
        this.connectPromise?.cancel();
        this.connectPromise = null;

        this.state.localTracks.forEach((track: any) => {
            track.stop?.();
        });
        this.setState({localTracks: []});

        if (room) {
            room.disconnect();
            this.setState({room: null});
        }
    };

    joinRoom = (token: string, notify = true) => {
        if (!this._isMounted) {
            return;
        }

        const {roomName, logLevel, mediaConstraints} = this.props;
        const {localTracks} = this.state;
        const connectOptions: Video.ConnectOptions = {
            name: roomName,
            preferredVideoCodecs: platformUtils.isSafari() ? ['H264', 'VP8'] : [],
            tracks: localTracks,
            networkQuality: {
                local: 1,
                remote: 1,
            },
            logLevel,
            audio: mediaConstraints?.audioinput,
            video: mediaConstraints?.videoinput && {width: 640, height: 480}
        };

        this.connectPromise = Video.connect(token, connectOptions);

        return this.connectPromise
            .then(
                (room) => {
                    const {_isMounted} = this;

                    if (!_isMounted) {
                        room.disconnect();
                        return;
                    }

                    this.setState({room});

                    // Once the local participant leaves the room, detach the Tracks
                    // of all other participants, including that of the LocalParticipant.
                    room.on('disconnected', (_room, error) => {
                        this.props.onDisconnect?.();

                        if (this._isMounted) {
                            this.setState({
                                room: null
                            });

                            if (error && SHOULD_RECONNECT_TWILIO_ERROR_CODES.includes(error.code)) {
                                logger.warn({category: logger.category.Twilio, message: 'disconnected. retry connection'});
                                this.joinRoomWithRetries(token, {retries: 6, minTimeout: 5000}, false);
                            }
                        }
                    });

                    if (notify) {
                        this.webRTCStartSuccess?.();
                    }
                }
            );
    };

    getTwilioStartSuccessStatus = (attempt: number): WebRTCConnectDetails =>
        this.isNotOfficiallySupported ?
            {
                code: NOT_OFFICIALLY_SUPPORTED,
                message: 'Browser is not officially supported',
                internal: true,
                attempt
            } : {attempt};

    openErrorDialog = (message: string) => {
        this.setState({errorDialogProps: {open: true, message}});
    };

    closeErrorDialog = () => {
        this.setState({errorDialogProps: {open: false, message: ''}});
    };

    render() {
        const {children, t} = this.props;
        const {room, errorDialogProps} = this.state;
        const {closeErrorDialog} = this;

        if (!room) {
            return (
                <Fragment>
                    <Loader className='tw-max-w-12 tw-max-h-12' />

                    <InformationModal
                        open={errorDialogProps.open}
                        fullWidth
                        maxWidth='sm'
                        title={t(T_KEYS.ROOM_ERROR_DIALOG.TITLE)}
                        onClose={closeErrorDialog}
                    >
                        <span>{t(errorDialogProps.message)}</span>
                        {errorDialogProps.message === T_KEYS.ROOM_ERROR_DIALOG.NOT_SUPPORTED &&
                            <a target='_blank' rel='noreferrer' href={TWILIO_SUPPORTED_BROWSERS_LINK}>
                                {t(T_KEYS.ROOM_ERROR_DIALOG.LINK_TEXT)}
                            </a>}
                    </InformationModal>
                </Fragment>
            );
        }

        return children({room});
    }
}

export default withTranslation()(TwilioRoom);
